import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { PLAN_INTENT_KEY, clearPlanIntent, PlanIntent } from './PlanIntentBanner';

const PRO_PRICES = { monthly: 9, annual: 7 };
const TEAM_TIERS = [
  { key: 'Starter',  monthly: 59,  annual: 49,  seats: 'Up to 10 members' },
  { key: 'Firm',     monthly: 129, annual: 109, seats: 'Up to 30 members' },
  { key: 'Practice', monthly: 249, annual: 199, seats: 'Up to 100 members' },
] as const;

const PRO_FEATURES = [
  'Unlimited renewal cycles',
  'Multi-state & profession tracking',
  'PDF compliance reports',
  'Certificate storage & retrieval',
  'Email renewal reminders',
];

const TEAM_FEATURES = [
  'Everything in Pro — for your whole team',
  'Centralized compliance dashboard',
  'Bulk CSV import for team classes',
  'Firm-level compliance reports',
  'Team admin & role management',
];

interface UpgradeModalProps {
  type: 'pro' | 'team';
  onClose: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ type, onClose }) => {
  const navigate = useNavigate();

  // Read saved plan intent to pre-populate
  const getSavedIntent = (): PlanIntent | null => {
    try { return JSON.parse(localStorage.getItem(PLAN_INTENT_KEY) || ''); } catch { return null; }
  };
  const saved = getSavedIntent();

  const [interval, setInterval] = useState<'monthly' | 'annual'>(
    (saved?.interval === 'annual' ? 'annual' : 'monthly') as 'monthly' | 'annual',
  );
  const [tier, setTier] = useState<string>(
    type === 'team' ? (saved?.tier || 'Starter') : '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tierData = TEAM_TIERS.find(t => t.key === tier) || TEAM_TIERS[0];
  const price = type === 'pro'
    ? PRO_PRICES[interval]
    : tierData[interval];
  const annualSavings = type === 'pro'
    ? (PRO_PRICES.monthly - PRO_PRICES.annual) * 12
    : (tierData.monthly - tierData.annual) * 12;

  const handleCheckout = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const payload: Record<string, string> = { plan: type, interval };
      if (type === 'team') payload.tier = tier;
      const { data } = await api.post('/api/billing/create-checkout-session', payload);

      if (data.activated) {
        // Stripe bypass — plan is now active
        clearPlanIntent();
        onClose();
        navigate('/billing?success=true');
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setError('Unexpected response from billing. Please try again.');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [type, interval, tier, navigate, onClose]);

  // Trap focus inside modal
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(15,31,61,0.55)', backdropFilter: 'blur(2px)' }}
      />

      {/* Panel */}
      <div style={{
        position: 'relative', zIndex: 1,
        background: 'var(--color-surface-raised, #fff)',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(15,31,61,0.28)',
        width: '100%', maxWidth: 480,
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          background: 'var(--color-primary, #0F1F3D)',
          padding: '1.5rem 1.75rem 1.25rem',
          color: '#fff',
        }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: '1rem', right: '1rem',
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', cursor: 'pointer', fontSize: '1rem',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
          >
            <i className="bi bi-x-lg" />
          </button>

          <div style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2ABFBF', marginBottom: '0.4rem' }}>
            {type === 'pro' ? 'Upgrade Required' : 'Team Plan Required'}
          </div>
          <h2 id="upgrade-modal-title" style={{ fontFamily: 'var(--font-display, Georgia)', fontWeight: 800, fontSize: 'clamp(1.25rem, 3vw, 1.75rem)', margin: 0, lineHeight: 1.15 }}>
            {type === 'pro' ? 'Unlock Unlimited Cycles' : 'Add Your First Team Member'}
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', opacity: 0.8, margin: '0.5rem 0 0' }}>
            {type === 'pro'
              ? 'The free plan includes one renewal cycle. Upgrade to Pro to track unlimited cycles across states and professions.'
              : 'Inviting team members requires a Team plan. Choose a tier that fits your firm size.'}
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem 1.75rem' }}>

          {/* Interval toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', background: 'var(--color-surface, #f5f5f5)', borderRadius: 10, padding: 4 }}>
            {(['monthly', 'annual'] as const).map(iv => (
              <button
                key={iv}
                onClick={() => setInterval(iv)}
                style={{
                  flex: 1, padding: '0.45rem 0.75rem', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontWeight: iv === interval ? 700 : 400, fontSize: '0.875rem',
                  background: iv === interval ? 'var(--color-surface-raised, #fff)' : 'transparent',
                  color: iv === interval ? 'var(--color-primary, #0F1F3D)' : 'var(--color-text-muted, #666)',
                  boxShadow: iv === interval ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {iv === 'monthly' ? 'Monthly' : (
                  <span>Annual <span style={{ fontSize: '0.75rem', color: '#2ABFBF', fontWeight: 700 }}>Save ${annualSavings}/yr</span></span>
                )}
              </button>
            ))}
          </div>

          {/* Tier selector (team only) */}
          {type === 'team' && (
            <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
              {TEAM_TIERS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTier(t.key)}
                  style={{
                    flex: 1, padding: '0.6rem 0.25rem', border: `2px solid ${tier === t.key ? '#2ABFBF' : 'var(--color-border, #ddd)'}`,
                    borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                    background: tier === t.key ? 'rgba(42,191,191,0.08)' : 'transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.82rem', color: tier === t.key ? '#2ABFBF' : 'var(--color-text, #333)' }}>
                    {t.key}
                  </div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.72rem', color: 'var(--color-text-muted, #888)', marginTop: 2 }}>
                    {t.seats.replace('Up to ', '')}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Pricing display */}
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: '0.375rem',
            marginBottom: '1.25rem',
          }}>
            <span style={{ fontFamily: 'var(--font-display, Georgia)', fontWeight: 800, fontSize: 'clamp(2rem, 6vw, 2.75rem)', color: 'var(--color-primary, #0F1F3D)', lineHeight: 1 }}>
              ${price}
            </span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--color-text-muted, #888)' }}>
              /mo{interval === 'annual' ? ' · billed annually' : ''}
            </span>
            {type === 'team' && (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'var(--color-text-muted, #888)', marginLeft: 4 }}>
                · {tierData.seats}
              </span>
            )}
          </div>

          {/* Feature list */}
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(type === 'pro' ? PRO_FEATURES : TEAM_FEATURES).map(f => (
              <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--color-text, #333)' }}>
                <span style={{ color: '#2ABFBF', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                {f}
              </li>
            ))}
          </ul>

          {/* Error */}
          {error && (
            <div style={{ marginBottom: '1rem', padding: '0.6rem 0.875rem', background: 'var(--color-danger-light, #fef2f2)', border: '1px solid var(--color-danger, #ef4444)', borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--color-danger, #b91c1c)' }}>
              <i className="bi bi-exclamation-circle me-2" />{error}
            </div>
          )}

          {/* CTA */}
          <button
            onClick={handleCheckout}
            disabled={loading}
            style={{
              width: '100%', padding: '0.8rem 1rem',
              background: loading ? '#94a3b8' : '#2ABFBF',
              color: '#fff', border: 'none', borderRadius: 10,
              fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
              marginBottom: '0.75rem',
            }}
            onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = '#24a8a8'; }}
            onMouseLeave={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = '#2ABFBF'; }}
          >
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Processing…</>
              : `Continue to checkout →`}
          </button>

          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '0.6rem 1rem',
              background: 'transparent', border: 'none',
              fontFamily: 'var(--font-body)', fontSize: '0.875rem',
              color: 'var(--color-text-muted, #888)', cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
